import type { State } from '../../store/types.js';

import {
  activityCodesAvailableHealth,
  currentEmployeeUuid,
  employeesNotDeleted,
  groupBy,
  guidelinesNotDeleted,
  isIssueProcessed,
  issuesNotDeleted,
  key,
  toIssueViewModel,
} from 'src/store/selectors';
import { LocalDate } from '../../utilities/local-date.js';
import type { ItemGroup } from 'src/pages/organization-page/organization-page-view-model';
import _ from 'lodash';
import { commonFieldsByCode } from 'src/store/fields';
import type { AbstractPageView } from 'src/pages/abstract-page-view';

import { getIssueLabel } from '../../store/selectors/issues/getIssueLabel';
import type { IssuesPageViewModel } from '../../pages/issues-page/issues-page-view-model';
import { BASE_PATH } from '../../store';
import type { PageLink } from 'src/pages/overview-page/overview-page-view-model';
import { issueGuidelineTemplateId } from 'src/store/selectors/templates';
import { peopleAndFunctions } from '../../store/selectors/assignees';
import { contactsNotDeleted } from '../../store/selectors/partners/contacts-not-deleted';
import { issueEscalationCandidates } from '../../store/selectors/issues/issue-escalation-candidates';
import { issueEscalationSettings } from '../../store/selectors/issues/issue-escalation-settings';
import type { SelectCheckboxOption } from '../../library/editors/elements/select-checkbox-option';
import { t } from 'src/i18n';

function formatDate(value: string | undefined) {
  if (value) {
    return LocalDate.fromString(value).toStringForDisplay();
  }
  return 'Uten dato';
}

function getGroupedIssues(state: State, hrefPrefix: string) {
  const groupNames = {
    ferdigbehandlet: 'Ferdigbehandlede',
    aktiv: 'Aktive',
  };
  const issues = issuesNotDeleted(state)
    .map((i) => toIssueViewModel(i))
    .map((i) => {
      return {
        ...i,
        reportedDateFormatted: formatDate(i.reportedDate),
        status: isIssueProcessed(i) ? 'ferdigbehandlet' : 'aktiv',
      };
    });
  const groupedIssues = groupBy(issues, (e) => e.status ?? '');
  const result: ItemGroup[] = _.sortBy(groupedIssues, [(g) => g.group !== 'aktiv', (g) => g.group]).map((g) => {
    return {
      label: groupNames[g.group],
      tooltip: commonFieldsByCode()['issues_closed'].tooltip,
      filtered: g.group === 'ferdigbehandlet',
      items: _.sortBy(g.items, (item) => item.reportedDate).map((item) => {
        const classification = item.classification ?? 'NONE';
        const accessControl = item.accessControl ?? [];
        const accessible =
          item.classification === 'NONE' ||
          (item.accessControl && item.accessControl.includes(currentEmployeeUuid(state) ?? ''));
        return {
          uuid: item.uuid,
          label: getIssueLabel(item, accessible ?? false, employeesNotDeleted(state)),
          dateDisplay: item.reportedDateFormatted,
          accessControl: accessControl,
          classification: classification,
          locked: classification !== 'NONE',
          accessible: accessible ?? false,
          href: hrefPrefix + 'issues/' + item.uuid,
          relatedItems: [],
          hasDraft: item.hasDraft,
        };
      }),
    };
  });
  return result;
}

/**
 * Ansatte og kontaktpersoner som kan velges som varslingsmottakere.
 * Bruker peopleAndFunctions for navn, men begrenser til aktive personer.
 */
function getNotifyPersonOptions(state: State): SelectCheckboxOption[] {
  const allowedUuids = new Set([
    ...employeesNotDeleted(state).map((e) => e.uuid),
    ...contactsNotDeleted(state).map((c) => c.uuid),
  ]);
  return peopleAndFunctions(state)
    .filter((p) => (p.type === 'employee' || p.type === 'contact') && allowedUuids.has(p.uuid))
    .map((p) => ({
      value: p.uuid,
      text: p.fullName,
      sublabel: p.type === 'contact' ? t('issues.escalation.roleContact') : t('issues.escalation.roleEmployee'),
    }));
}

export function issuesPageView(hrefPrefix: string, viewModel: AbstractPageView, state: State): IssuesPageViewModel {
  const o = state.organization;
  if (o === undefined) {
    throw new Error('Illegal state (E151), organization not found');
  }
  let pageLinks: PageLink[] = [];
  const issueGuideline = guidelinesNotDeleted(state).find((g) => {
    return g.mainTemplateId === issueGuidelineTemplateId;
  });
  if (issueGuideline) {
    pageLinks = [
      {
        type: 'guidelines',
        name: issueGuideline.name ?? '',
        href: hrefPrefix + 'guidelines/' + issueGuideline.uuid,
      },
    ];
  }
  return {
    ...viewModel,
    type: 'issues-page',
    icon: 'issues',
    href: hrefPrefix,
    availableActivityCodes: activityCodesAvailableHealth(),
    pdfHref: BASE_PATH + '/organizations/' + o.organizationId + '/overview.pdf?key=' + key(state),
    issues: getGroupedIssues(state, hrefPrefix),
    pageLinks,
    escalationCandidates: issueEscalationCandidates(state, hrefPrefix),
    escalationSettings: issueEscalationSettings(state),
    notifyPersonOptions: getNotifyPersonOptions(state),
  };
}
